import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  GraduationCap, LogOut, BookOpen, Clock, Trophy, Target, 
  Flame, TrendingUp, Award, FileText, CheckCircle2, Download,
  Calendar, Activity, Zap, Brain, MessageSquare, BookMarked,
  ClipboardList, User, Users, ArrowRight, RefreshCw, Star, BookCheck, PlayCircle,
  Edit3, Camera, Mail, Phone, MapPin, Briefcase, Settings, Bell,
  ChevronRight, MoreHorizontal, Eye, EyeOff, Save, X, Sparkles,
  PieChart, BarChart2, TrendingDown, AlertCircle, Gift, Medal, Crown,
  FileText as FileTextIcon, Tag, ChevronDown, Lightbulb, HelpCircle
} from 'lucide-react';
import Header from '../components/Header';
import MessagingPanel from '../components/MessagingPanel';
import QuizHistory from '../components/QuizHistory';
import FormativeFeedback from '../components/FormativeFeedback';
import CognitiveGrowthGraph from '../components/CognitiveGrowthGraph';
import LearningInsights from '../components/LearningInsights';
import ResourcesHub from '../components/ResourcesHub';
import SkillsDashboard from '../components/SkillsDashboard';
import PeerInsights from '../components/PeerInsights';
import axios from 'axios';
import { toast } from "sonner";

export default function ProfileAnalytics() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [courseDetails, setCourseDetails] = useState([]);
  const [userStats, setUserStats] = useState({
    total_study_minutes: 0,
    total_study_hours: 0,
    skills_mastered: 0,
    tasks_completed: 0,
    current_day_streak: 0,
    longest_day_streak: 0,
    total_sessions: 0,
    courses_completed: 0,
    modules_completed: 0
  });
  const [advancedStats, setAdvancedStats] = useState({
    avg_session_duration: 0,
    sessions_this_week: 0,
    most_active_day: 'N/A',
    peak_study_hour: '12:00',
    learning_velocity: 0,
    assignment_completion_rate: 0,
    best_performing_course: null,
    best_course_progress: 0,
    total_topics_mastered: 0,
    consistency_score: 0,
    weekly_progress_trend: 0,
    study_time_this_week: 0,
    courses_in_progress: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [institutions, setInstitutions] = useState({});
  const [enrolling, setEnrolling] = useState({});
  const [sessionSummaries, setSessionSummaries] = useState({});
  const [loadingSessions, setLoadingSessions] = useState({});
  const [expandedSessions, setExpandedSessions] = useState({});
  const [myGrades, setMyGrades] = useState([]);
  const [gradesLoading, setGradesLoading] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [weeklyActivityData, setWeeklyActivityData] = useState([]);
  const [myTeachers, setMyTeachers] = useState([]);
  
  // Edit profile state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    bio: '',
    avatar_url: '',
    location: '',
    occupation: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [token, setToken] = useState(() => localStorage.getItem('faheem_token'));
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  // Re-check token on mount and when authChange event fires
  useEffect(() => {
    const updateToken = () => {
      const newToken = localStorage.getItem('faheem_token');
      setToken(newToken);
    };
    
    updateToken();
    window.addEventListener('authChange', updateToken);
    window.addEventListener('storage', updateToken);
    
    return () => {
      window.removeEventListener('authChange', updateToken);
      window.removeEventListener('storage', updateToken);
    };
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('faheem_token');
    navigate('/login');
  }, [navigate]);

  const formatTime = (minutes) => {
    if (!minutes || minutes === 0) return '0m';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Format session summary text with proper styling
  const formatSummaryText = (text) => {
    if (!text) return null;
    
    // Remove separator lines (=== or ---)
    let cleanText = text
      .replace(/={3,}/g, '')
      .replace(/-{3,}/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    // Split into sections
    const sections = [];
    const lines = cleanText.split('\n');
    let currentSection = { title: null, content: [] };
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        if (currentSection.content.length > 0) {
          sections.push({ ...currentSection });
          currentSection = { title: null, content: [] };
        }
        return;
      }
      
      // Check if this is a header (ends with colon or is all caps or starts with **)
      const isHeader = /^(\*\*.*\*\*:?|[A-Z][A-Z\s]+:?|.*:)$/.test(trimmedLine) && trimmedLine.length < 60;
      
      if (isHeader && currentSection.content.length === 0) {
        currentSection.title = trimmedLine.replace(/^\*\*|\*\*$/g, '').replace(/:$/, '');
      } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ') || trimmedLine.match(/^\d+\./)) {
        // It's a list item
        currentSection.content.push({
          type: 'list',
          text: trimmedLine.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '')
        });
      } else {
        // Regular paragraph
        currentSection.content.push({
          type: 'text',
          text: trimmedLine
        });
      }
    });
    
    // Push last section
    if (currentSection.content.length > 0 || currentSection.title) {
      sections.push(currentSection);
    }
    
    // Render formatted sections
    return (
      <div className="space-y-4">
        {sections.map((section, idx) => (
          <div key={idx}>
            {section.title && (
              <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                {section.title}
              </h5>
            )}
            {section.content.length > 0 && (
              <div className={section.title ? 'pl-4' : ''}>
                {section.content.some(c => c.type === 'list') ? (
                  <ul className="space-y-2">
                    {section.content.map((item, itemIdx) => (
                      <li key={itemIdx} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{formatInlineText(item.text)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  section.content.map((item, itemIdx) => (
                    <p key={itemIdx} className="text-sm text-slate-700 leading-relaxed mb-2">
                      {formatInlineText(item.text)}
                    </p>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Format inline text (bold, italic, code)
  const formatInlineText = (text) => {
    if (!text) return text;
    
    // Split by bold markers (**text** or __text__)
    const parts = text.split(/(\*\*.*?\*\*|__.*?__)/g);
    
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return <strong key={idx} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
      }
      // Handle inline code
      if (part.includes('`')) {
        const codeParts = part.split(/(`[^`]+`)/g);
        return codeParts.map((codePart, codeIdx) => {
          if (codePart.startsWith('`') && codePart.endsWith('`')) {
            return <code key={`${idx}-${codeIdx}`} className="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono text-indigo-600">{codePart.slice(1, -1)}</code>;
          }
          return codePart;
        });
      }
      return part;
    });
  };

  // Handle profile update
  const handleProfileUpdate = async () => {
    if (!token) {
      toast.error('Please log in to update profile');
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/supabase-auth/profile`,
        {
          username: editForm.username,
          bio: editForm.bio,
          avatar_url: editForm.avatar_url,
          location: editForm.location,
          occupation: editForm.occupation
        },
        {
          params: { access_token: token }
        }
      );

      setProfile(prev => ({
        ...prev,
        username: editForm.username,
        bio: editForm.bio,
        avatar_url: editForm.avatar_url,
        location: editForm.location,
        occupation: editForm.occupation
      }));

      toast.success('Profile updated successfully!');
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Open edit dialog with current values
  const openEditDialog = () => {
    setEditForm({
      username: profile?.username || '',
      bio: profile?.bio || '',
      avatar_url: profile?.avatar_url || '',
      location: profile?.location || '',
      occupation: profile?.occupation || ''
    });
    setIsEditDialogOpen(true);
  };

  // Handle course enrollment
  const handleEnroll = async (courseId, courseTitle) => {
    if (!token) {
      toast.error('Please log in to enroll');
      return;
    }

    setEnrolling(prev => ({ ...prev, [courseId]: true }));

    try {
      await axios.post(
        `${BACKEND_URL}/api/supabase-auth/enroll`,
        { course_id: courseId },
        { params: { access_token: token } }
      );

      toast.success(`Enrolled in ${courseTitle}!`);
      await loadAllData();
    } catch (error) {
      console.error('Enrollment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to enroll in course');
    } finally {
      setEnrolling(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Handle course unenrollment
  const handleUnenroll = async (courseId, courseTitle) => {
    if (!token) {
      toast.error('Please log in');
      return;
    }

    if (!window.confirm(`Are you sure you want to unenroll from "${courseTitle}"? Your progress will be saved.`)) {
      return;
    }

    setEnrolling(prev => ({ ...prev, [courseId]: true }));

    try {
      await axios.post(
        `${BACKEND_URL}/api/supabase-auth/unenroll`,
        { course_id: courseId },
        { params: { access_token: token } }
      );

      toast.success(`Unenrolled from ${courseTitle}`);
      await loadAllData();
    } catch (error) {
      console.error('Unenrollment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to unenroll from course');
    } finally {
      setEnrolling(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Fetch session summaries for a course
  const fetchSessionSummaries = async (courseId) => {
    if (!token || sessionSummaries[courseId]) return;
    
    setLoadingSessions(prev => ({ ...prev, [courseId]: true }));
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/sessions/?access_token=${token}&course_id=${courseId}&limit=50`
      );
      
      setSessionSummaries(prev => ({
        ...prev,
        [courseId]: response.data?.sessions || []
      }));
    } catch (error) {
      console.error(`Error fetching sessions for course ${courseId}:`, error);
      setSessionSummaries(prev => ({
        ...prev,
        [courseId]: []
      }));
    } finally {
      setLoadingSessions(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Toggle session expansion and fetch data
  const toggleSessionExpansion = (courseId) => {
    const isExpanded = expandedSessions[courseId];
    setExpandedSessions(prev => ({
      ...prev,
      [courseId]: !isExpanded
    }));
    
    if (!isExpanded && !sessionSummaries[courseId]) {
      fetchSessionSummaries(courseId);
    }
  };

  // Format session date
  const formatSessionDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch all data
  const loadAllData = useCallback(async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);

      // CRITICAL DATA ONLY - Load first for fast initial render
      const [profileRes, coursesRes, institutionsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/supabase-auth/profile?access_token=${token}`),
        axios.get(`${BACKEND_URL}/api/courses/`),
        axios.get(`${BACKEND_URL}/api/institutions/`),
        axios.get(`${BACKEND_URL}/api/analytics/stats?access_token=${token}`)
      ]);

      // Set critical state immediately
      setProfile(profileRes.data);
      setUserStats(statsRes.data);

      const institutionsList = institutionsRes.data?.institutions || [];
      const institutionsMap = {};
      institutionsList.forEach(inst => {
        institutionsMap[inst.id] = inst;
      });
      setInstitutions(institutionsMap);

      const enrolledCourseIds = profileRes.data.enrolled_courses || [];
      const fetchedCourses = coursesRes.data?.courses || [];
      setAllCourses(fetchedCourses);
      const enrolledCourses = fetchedCourses.filter(c => enrolledCourseIds.includes(c.id));
      setCourses(enrolledCourses);

      // Stop loading spinner - show UI with basic data
      setLoading(false);

      // DEFERRED DATA - Load in background after initial render
      Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/recent-activities?access_token=${token}&limit=10`).catch(() => ({ data: { activities: [] } })),
        axios.get(`${BACKEND_URL}/api/analytics/advanced-stats?access_token=${token}`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/assignments/grades/my?access_token=${token}`).catch(() => ({ data: { grades: [] } })),
        axios.get(`${BACKEND_URL}/api/teacher/check?access_token=${token}`).catch(() => ({ data: { is_teacher: false } })),
        axios.get(`${BACKEND_URL}/api/analytics/weekly-activity?access_token=${token}`).catch(() => ({ data: { weekly_data: [] } })),
        axios.get(`${BACKEND_URL}/api/messages/my-teachers/?access_token=${token}`).catch(() => ({ data: { teachers: [] } }))
      ]).then(([activitiesRes, advancedStatsRes, gradesRes, teacherCheckRes, weeklyRes, teachersRes]) => {
        setRecentActivities(activitiesRes.data?.activities || []);
        setAdvancedStats(advancedStatsRes.data || {});
        setMyGrades(gradesRes.data?.grades || []);
        setIsTeacher(teacherCheckRes.data?.is_teacher || false);
        setWeeklyActivityData(weeklyRes.data?.weekly_data || []);
        setMyTeachers(teachersRes.data?.teachers || []);
      });

      // COURSE DETAILS - Load in background
      if (enrolledCourses.length > 0) {
        const courseProgressPromises = enrolledCourses.map(async (course) => {
          try {
            const [topicsRes, assignmentsRes] = await Promise.all([
              axios.get(`${BACKEND_URL}/api/course-progress/topics/${course.id}?access_token=${token}`),
              axios.get(`${BACKEND_URL}/api/assignments/course/${course.id}?access_token=${token}`)
            ]);

            return {
              ...course,
              topicsStats: topicsRes.data?.stats || {},
              assignmentsStats: {
                total: assignmentsRes.data?.total || 0,
                completed: assignmentsRes.data?.completed || 0,
                completion_percentage: assignmentsRes.data?.completion_percentage || 0
              },
              topics: topicsRes.data?.topics || [],
              assignments: assignmentsRes.data?.assignments || []
            };
          } catch (error) {
            console.error(`Error fetching details for course ${course.id}:`, error);
            return { ...course, topicsStats: {}, assignmentsStats: {}, topics: [], assignments: [] };
          }
        });

        Promise.all(courseProgressPromises).then(detailedCourses => {
          setCourseDetails(detailedCourses);
        });
      } else {
        setCourseDetails([]);
      }

    } catch (error) {
      console.error('Error loading profile data:', error);
      setLoading(false);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        handleLogout();
      } else {
        toast.error('Failed to load some profile data.');
      }
    }
  }, [token, BACKEND_URL, navigate, handleLogout]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Calculations
  const totalTopicsCompleted = courseDetails.reduce((sum, c) => sum + (c.topicsStats?.completed_topics || 0), 0);
  const totalTopics = courseDetails.reduce((sum, c) => sum + (c.topicsStats?.total_topics || 0), 0);
  const totalAssignmentsCompleted = courseDetails.reduce((sum, c) => sum + (c.assignmentsStats?.completed || 0), 0);
  const totalAssignments = courseDetails.reduce((sum, c) => sum + (c.assignmentsStats?.total || 0), 0);
  const overallTopicsPercent = totalTopics > 0 ? Math.round((totalTopicsCompleted / totalTopics) * 100) : 0;
  const overallAssignmentsPercent = totalAssignments > 0 ? Math.round((totalAssignmentsCompleted / totalAssignments) * 100) : 0;
  const overallProgress = Math.round((overallTopicsPercent + overallAssignmentsPercent) / 2);
  
  // Student level calculation
  const studentLevel = Math.min(Math.floor(overallProgress / 20) + 1, 5);
  const currentLevelProgress = overallProgress % 20;
  const progressToNextLevel = (currentLevelProgress / 20) * 100;

  // Level info
  const levelInfo = {
    1: { name: 'Beginner', color: 'bg-slate-500', icon: Star },
    2: { name: 'Learner', color: 'bg-blue-500', icon: BookOpen },
    3: { name: 'Achiever', color: 'bg-purple-500', icon: Award },
    4: { name: 'Expert', color: 'bg-orange-500', icon: Medal },
    5: { name: 'Master', color: 'bg-yellow-500', icon: Crown }
  };

  // Weekly activity data - use real data from API, fallback to empty
  const weeklyData = weeklyActivityData.length > 0 
    ? weeklyActivityData.map(d => ({ day: d.day, minutes: d.minutes }))
    : [
        { day: 'Mon', minutes: 0 },
        { day: 'Tue', minutes: 0 },
        { day: 'Wed', minutes: 0 },
        { day: 'Thu', minutes: 0 },
        { day: 'Fri', minutes: 0 },
        { day: 'Sat', minutes: 0 },
        { day: 'Sun', minutes: 0 },
      ];

  // Course progress data for chart
  const courseProgressData = courseDetails.map(course => ({
    name: course.title?.substring(0, 12) + '...' || 'Course',
    fullName: course.title || 'Course',
    progress: Math.round(((course.topicsStats?.completion_percentage || 0) + (course.assignmentsStats?.completion_percentage || 0)) / 2)
  }));

  // Activity formatting
  const activityTypeLabels = {
    'course_start': 'Started Course',
    'course_complete': 'Completed Course',
    'module_complete': 'Completed Topic',
    'module_start': 'Started Topic',
    'session_record': 'Study Session',
    'login': 'Logged In',
    'assignment_complete': 'Completed Assignment',
    'quiz_complete': 'Completed Quiz'
  };

  const activityTypeIcons = {
    'course_start': BookOpen,
    'course_complete': Trophy,
    'module_complete': CheckCircle2,
    'module_start': PlayCircle,
    'session_record': Clock,
    'login': User,
    'assignment_complete': ClipboardList,
    'quiz_complete': Award
  };

  const formatActivityDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Smart recommendations based on progress
  const getRecommendations = () => {
    const recommendations = [];
    
    if (courses.length === 0) {
      recommendations.push({
        type: 'action',
        icon: BookOpen,
        title: 'Start Your Journey',
        description: 'Enroll in your first course to begin learning',
        action: '/courses',
        color: 'blue'
      });
    }
    
    if (userStats?.current_day_streak === 0) {
      recommendations.push({
        type: 'streak',
        icon: Flame,
        title: 'Build Your Streak',
        description: 'Study today to start a learning streak',
        color: 'orange'
      });
    }
    
    if (overallProgress > 0 && overallProgress < 50) {
      recommendations.push({
        type: 'progress',
        icon: TrendingUp,
        title: 'Keep Going!',
        description: `You're ${50 - overallProgress}% away from halfway`,
        color: 'green'
      });
    }

    if (totalAssignmentsCompleted < totalAssignments) {
      recommendations.push({
        type: 'assignment',
        icon: ClipboardList,
        title: 'Pending Assignments',
        description: `${totalAssignments - totalAssignmentsCompleted} assignments waiting`,
        color: 'purple'
      });
    }

    return recommendations.slice(0, 3);
  };

  const recommendations = getRecommendations();
  const LevelIcon = levelInfo[studentLevel]?.icon || Star;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Profile Header Section */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Profile Image & Basic Info */}
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="relative group">
                <div className="w-28 h-28 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 p-1">
                  {profile?.avatar_url ? (
                    <img 
                      src={profile.avatar_url} 
                      alt={profile?.username || 'User'} 
                      className="w-full h-full rounded-xl object-cover bg-white"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center ${profile?.avatar_url ? 'hidden' : 'flex'}`}
                  >
                    <span className="text-4xl font-bold text-blue-600">
                      {(profile?.username || 'S')[0].toUpperCase()}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={openEditDialog}
                  className="absolute -bottom-2 -right-2 bg-white border-2 border-slate-200 p-2 rounded-full shadow-lg hover:bg-slate-50 transition-colors"
                >
                  <Edit3 className="w-4 h-4 text-slate-600" />
                </button>
              </div>

              {/* User Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900">
                    {profile?.username || 'Student'}
                  </h1>
                  <Badge className={`${levelInfo[studentLevel]?.color} text-white`}>
                    <LevelIcon className="w-3 h-3 mr-1" />
                    {levelInfo[studentLevel]?.name}
                  </Badge>
                </div>
                
                {profile?.bio && (
                  <p className="text-slate-600 text-sm max-w-md">{profile.bio}</p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  {profile?.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {profile.email}
                    </span>
                  )}
                  {profile?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profile.location}
                    </span>
                  )}
                  {profile?.occupation && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {profile.occupation}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={openEditDialog}>
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadAllData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 md:ml-auto">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{courses?.length || 0}</p>
                <p className="text-xs text-slate-600 mt-1">Enrolled</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{userStats?.total_study_hours || 0}h</p>
                <p className="text-xs text-slate-600 mt-1">Study Time</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{totalTopicsCompleted}</p>
                <p className="text-xs text-slate-600 mt-1">Topics Done</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">{userStats?.current_day_streak || 0}</p>
                <p className="text-xs text-slate-600 mt-1">Day Streak</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border p-1 h-auto w-full flex rounded-xl shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <BarChart2 className="w-4 h-4 mr-1.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <BookOpen className="w-4 h-4 mr-1.5" />
              Courses
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Activity className="w-4 h-4 mr-1.5" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="explore" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Sparkles className="w-4 h-4 mr-1.5" />
              Explore
            </TabsTrigger>
            <TabsTrigger value="grades" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Award className="w-4 h-4 mr-1.5" />
              Grades
              {myGrades.length > 0 && (
                <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                  {myGrades.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="insights" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Sparkles className="w-4 h-4 mr-1.5" />
              Insights
            </TabsTrigger>
            <TabsTrigger value="skills" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Target className="w-4 h-4 mr-1.5" />
              Skills
            </TabsTrigger>
            <TabsTrigger value="peers" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <Users className="w-4 h-4 mr-1.5" />
              Peers
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <MessageSquare className="w-4 h-4 mr-1.5" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="resources" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white flex-1 text-center justify-center rounded-lg py-2.5 text-sm font-medium transition-all">
              <BookMarked className="w-4 h-4 mr-1.5" />
              Resources
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Level Progress Card - Dark Blue Theme */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Current Level</p>
                    <h2 className="text-3xl font-bold flex items-center gap-3">
                      <LevelIcon className="w-8 h-8" />
                      Level {studentLevel} - {levelInfo[studentLevel]?.name}
                    </h2>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm mb-1">Overall Progress</p>
                    <p className="text-4xl font-bold">{overallProgress}%</p>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progress to Level {Math.min(studentLevel + 1, 5)}</span>
                    <span>{Math.round(progressToNextLevel)}%</span>
                  </div>
                  <div className="bg-white/20 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-white h-full rounded-full transition-all duration-1000"
                      style={{ width: `${progressToNextLevel}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Study Time</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {userStats?.total_study_hours || 0}h {(userStats?.total_study_minutes || 0) % 60}m
                      </p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-xl">
                      <Clock className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Progress value={Math.min((userStats?.total_study_hours || 0) / 100 * 100, 100)} className="h-2" />
                    <p className="text-xs text-slate-500 mt-2">Keep up the great work!</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Topics Completed</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {totalTopicsCompleted}/{totalTopics}
                      </p>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-xl">
                      <Brain className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Progress value={overallTopicsPercent} className="h-2" />
                    <p className="text-xs text-slate-500 mt-2">{overallTopicsPercent}% complete</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Assignments</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {totalAssignmentsCompleted}/{totalAssignments}
                      </p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-xl">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Progress value={overallAssignmentsPercent} className="h-2" />
                    <p className="text-xs text-slate-500 mt-2">{overallAssignmentsPercent}% complete</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Day Streak</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
                        {userStats?.current_day_streak || 0}
                        <Flame className="w-5 h-5 text-orange-500" />
                      </p>
                    </div>
                    <div className="bg-orange-100 p-3 rounded-xl">
                      <Flame className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs text-slate-500">Best: {userStats?.longest_day_streak || 0} days</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Advanced Statistics Grid */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-indigo-600" />
                  Advanced Learning Analytics
                </CardTitle>
                <CardDescription>Detailed insights into your learning patterns and performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {/* Learning Velocity */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-blue-500 p-1.5 rounded-lg">
                        <Zap className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-blue-700">Learning Velocity</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{advancedStats?.learning_velocity || 0}</p>
                    <p className="text-xs text-blue-600 mt-1">topics/week</p>
                  </div>

                  {/* Avg Session Duration */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-purple-500 p-1.5 rounded-lg">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-purple-700">Avg Session</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-900">{advancedStats?.avg_session_duration || 0}m</p>
                    <p className="text-xs text-purple-600 mt-1">per session</p>
                  </div>

                  {/* Most Active Day */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-green-500 p-1.5 rounded-lg">
                        <Calendar className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-green-700">Most Active Day</span>
                    </div>
                    <p className="text-2xl font-bold text-green-900">{advancedStats?.most_active_day || 'N/A'}</p>
                    <p className="text-xs text-green-600 mt-1">day of week</p>
                  </div>

                  {/* Peak Study Hour */}
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-amber-500 p-1.5 rounded-lg">
                        <Clock className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-amber-700">Peak Hour</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-900">{advancedStats?.peak_study_hour || '12:00'}</p>
                    <p className="text-xs text-amber-600 mt-1">most productive</p>
                  </div>

                  {/* Sessions This Week */}
                  <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-4 border border-cyan-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-cyan-500 p-1.5 rounded-lg">
                        <Activity className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-cyan-700">This Week</span>
                    </div>
                    <p className="text-2xl font-bold text-cyan-900">{advancedStats?.sessions_this_week || 0}</p>
                    <p className="text-xs text-cyan-600 mt-1">sessions</p>
                  </div>

                  {/* Assignment Completion Rate */}
                  <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl p-4 border border-rose-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-rose-500 p-1.5 rounded-lg">
                        <Target className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-rose-700">Assignment Rate</span>
                    </div>
                    <p className="text-2xl font-bold text-rose-900">{advancedStats?.assignment_completion_rate || 0}%</p>
                    <p className="text-xs text-rose-600 mt-1">completion</p>
                  </div>

                  {/* Weekly Progress Trend */}
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-emerald-500 p-1.5 rounded-lg">
                        {(advancedStats?.weekly_progress_trend || 0) >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-white" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-xs font-medium text-emerald-700">Weekly Trend</span>
                    </div>
                    <p className={`text-2xl font-bold ${(advancedStats?.weekly_progress_trend || 0) >= 0 ? 'text-emerald-900' : 'text-red-600'}`}>
                      {(advancedStats?.weekly_progress_trend || 0) >= 0 ? '+' : ''}{advancedStats?.weekly_progress_trend || 0}%
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">vs last week</p>
                  </div>

                  {/* Total Topics Mastered */}
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-indigo-500 p-1.5 rounded-lg">
                        <Brain className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-indigo-700">Topics Mastered</span>
                    </div>
                    <p className="text-2xl font-bold text-indigo-900">{advancedStats?.total_topics_mastered || 0}</p>
                    <p className="text-xs text-indigo-600 mt-1">total mastered</p>
                  </div>

                  {/* Consistency Score */}
                  <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-4 border border-violet-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-violet-500 p-1.5 rounded-lg">
                        <Award className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-violet-700">Consistency</span>
                    </div>
                    <p className="text-2xl font-bold text-violet-900">{advancedStats?.consistency_score || 0}</p>
                    <p className="text-xs text-violet-600 mt-1">score (0-100)</p>
                  </div>

                  {/* Best Performing Course */}
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-yellow-500 p-1.5 rounded-lg">
                        <Trophy className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium text-yellow-700">Best Course</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-900">{advancedStats?.best_course_progress || 0}%</p>
                    <p className="text-xs text-yellow-600 mt-1 truncate" title={advancedStats?.best_performing_course || 'N/A'}>
                      {advancedStats?.best_performing_course ? 'progress' : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Weekly Activity Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Weekly Study Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={weeklyData}>
                      <defs>
                        <linearGradient id="colorMinutes" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fill: '#64748b', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="minutes" 
                        stroke="#3b82f6" 
                        fillOpacity={1} 
                        fill="url(#colorMinutes)" 
                        name="Minutes"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Course Progress Chart */}
              {courseProgressData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart2 className="w-5 h-5 text-purple-600" />
                      Course Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={courseProgressData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 12 }} />
                        <YAxis type="category" dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} width={100} />
                        <Tooltip 
                          formatter={(value) => [`${value}%`, 'Progress']}
                          labelFormatter={(value) => {
                            const item = courseProgressData.find(d => d.name === value);
                            return item?.fullName || value;
                          }}
                        />
                        <Bar dataKey="progress" fill="#8b5cf6" radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-500" />
                    Smart Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {recommendations.map((rec, idx) => {
                      const RecIcon = rec.icon;
                      return (
                        <div 
                          key={idx}
                          className={`p-4 rounded-xl border-2 border-dashed border-${rec.color}-200 bg-${rec.color}-50/50 hover:bg-${rec.color}-50 transition-colors cursor-pointer`}
                        >
                          <div className={`w-10 h-10 rounded-lg bg-${rec.color}-100 flex items-center justify-center mb-3`}>
                            <RecIcon className={`w-5 h-5 text-${rec.color}-600`} />
                          </div>
                          <h4 className="font-semibold text-slate-900 mb-1">{rec.title}</h4>
                          <p className="text-sm text-slate-600">{rec.description}</p>
                          {rec.action && (
                            <Link to={rec.action}>
                              <Button size="sm" variant="ghost" className="mt-2 p-0 h-auto text-blue-600">
                                Take Action <ChevronRight className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">My Enrolled Courses</h2>
              <Link to="/courses">
                <Button variant="outline">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Browse All Courses
                </Button>
              </Link>
            </div>

            {courseDetails.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {courseDetails.map((course) => {
                  const courseProgress = Math.round(((course.topicsStats?.completion_percentage || 0) + (course.assignmentsStats?.completion_percentage || 0)) / 2);
                  const courseSessions = sessionSummaries[course.id] || [];
                  const isSessionsExpanded = expandedSessions[course.id];
                  const isLoadingSessions = loadingSessions[course.id];
                  
                  return (
                    <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-500" />
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-slate-900 text-lg">{course.title}</h3>
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{course.description}</p>
                          </div>
                          <Badge variant="outline">{course.level || 'Beginner'}</Badge>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-600">Overall Progress</span>
                              <span className="font-semibold text-slate-900">{courseProgress}%</span>
                            </div>
                            <Progress value={courseProgress} className="h-2" />
                          </div>

                          <div className="grid grid-cols-3 gap-3 text-center">
                            {/* Topics Circular Progress */}
                            <div className="bg-slate-50 rounded-xl p-3 flex flex-col items-center">
                              <div className="relative w-16 h-16 mb-2">
                                <svg className="w-16 h-16 transform -rotate-90">
                                  <circle 
                                    cx="32" 
                                    cy="32" 
                                    r="28" 
                                    stroke="#e2e8f0" 
                                    strokeWidth="6" 
                                    fill="none" 
                                  />
                                  <circle
                                    cx="32"
                                    cy="32"
                                    r="28"
                                    stroke="#3b82f6"
                                    strokeWidth="6"
                                    fill="none"
                                    strokeDasharray={`${2 * Math.PI * 28}`}
                                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - (course.topicsStats?.completion_percentage || 0) / 100)}`}
                                    strokeLinecap="round"
                                    className="transition-all duration-700"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-sm font-bold text-blue-600">
                                    {course.topicsStats?.completion_percentage || 0}%
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs font-medium text-slate-700">Topics</p>
                              <p className="text-[10px] text-slate-500">
                                {course.topicsStats?.completed_topics || 0}/{course.topicsStats?.total_topics || 0}
                              </p>
                            </div>

                            {/* Assignments Circular Progress */}
                            <div className="bg-slate-50 rounded-xl p-3 flex flex-col items-center">
                              <div className="relative w-16 h-16 mb-2">
                                <svg className="w-16 h-16 transform -rotate-90">
                                  <circle 
                                    cx="32" 
                                    cy="32" 
                                    r="28" 
                                    stroke="#e2e8f0" 
                                    strokeWidth="6" 
                                    fill="none" 
                                  />
                                  <circle
                                    cx="32"
                                    cy="32"
                                    r="28"
                                    stroke="#8b5cf6"
                                    strokeWidth="6"
                                    fill="none"
                                    strokeDasharray={`${2 * Math.PI * 28}`}
                                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - (course.assignmentsStats?.completion_percentage || 0) / 100)}`}
                                    strokeLinecap="round"
                                    className="transition-all duration-700"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-sm font-bold text-purple-600">
                                    {course.assignmentsStats?.completion_percentage || 0}%
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs font-medium text-slate-700">Assignments</p>
                              <p className="text-[10px] text-slate-500">
                                {course.assignmentsStats?.completed || 0}/{course.assignmentsStats?.total || 0}
                              </p>
                            </div>

                            {/* Time Circular Display */}
                            <div className="bg-slate-50 rounded-xl p-3 flex flex-col items-center">
                              <div className="relative w-16 h-16 mb-2">
                                <svg className="w-16 h-16 transform -rotate-90">
                                  <circle 
                                    cx="32" 
                                    cy="32" 
                                    r="28" 
                                    stroke="#e2e8f0" 
                                    strokeWidth="6" 
                                    fill="none" 
                                  />
                                  <circle
                                    cx="32"
                                    cy="32"
                                    r="28"
                                    stroke="#10b981"
                                    strokeWidth="6"
                                    fill="none"
                                    strokeDasharray={`${2 * Math.PI * 28}`}
                                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - Math.min((course.topicsStats?.total_study_time_minutes || 0) / 300, 1))}`}
                                    strokeLinecap="round"
                                    className="transition-all duration-700"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Clock className="w-5 h-5 text-green-600" />
                                </div>
                              </div>
                              <p className="text-xs font-medium text-slate-700">Time</p>
                              <p className="text-[10px] text-slate-500 font-semibold">
                                {formatTime(course.topicsStats?.total_study_time_minutes || 0)}
                              </p>
                            </div>
                          </div>

                          {/* Session Summaries Section */}
                          <div className="border-t pt-4">
                            <button
                              onClick={() => toggleSessionExpansion(course.id)}
                              className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 via-purple-50 to-blue-50 rounded-xl hover:from-indigo-100 hover:via-purple-100 hover:to-blue-100 transition-all border border-indigo-100"
                            >
                              <div className="flex items-center gap-3">
                                <div className="bg-indigo-100 p-2.5 rounded-lg">
                                  <FileTextIcon className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div className="text-left">
                                  <span className="font-semibold text-slate-800">Learning Sessions</span>
                                  <p className="text-xs text-slate-500">View your study session history & summaries</p>
                                </div>
                                {courseSessions.length > 0 && (
                                  <Badge className="bg-indigo-600 text-white hover:bg-indigo-600 ml-2">
                                    {courseSessions.length} sessions
                                  </Badge>
                                )}
                              </div>
                              <ChevronDown className={`w-5 h-5 text-indigo-500 transition-transform duration-300 ${isSessionsExpanded ? 'rotate-180' : ''}`} />
                            </button>

                            {isSessionsExpanded && (
                              <div className="mt-4 space-y-4">
                                {isLoadingSessions ? (
                                  <div className="flex flex-col items-center justify-center py-10 bg-slate-50 rounded-xl">
                                    <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mb-3" />
                                    <span className="text-slate-600 font-medium">Loading your sessions...</span>
                                  </div>
                                ) : courseSessions.length > 0 ? (
                                  <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
                                    {courseSessions.map((session, idx) => (
                                      <div 
                                        key={session.id || idx}
                                        className="bg-white border-2 border-slate-100 rounded-xl p-5 hover:border-indigo-200 hover:shadow-lg transition-all"
                                      >
                                        {/* Session Header */}
                                        <div className="flex items-start justify-between mb-4 pb-3 border-b border-slate-100">
                                          <div className="flex items-center gap-3">
                                            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-3 rounded-xl shadow-md">
                                              <MessageSquare className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                              <p className="font-bold text-slate-900 text-lg">
                                                Session {courseSessions.length - idx}
                                              </p>
                                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                                <Calendar className="w-3.5 h-3.5" />
                                                {formatSessionDate(session.session_date || session.created_at)}
                                              </p>
                                            </div>
                                          </div>
                                          {session.duration_minutes > 0 && (
                                            <Badge className="bg-green-100 text-green-700 border border-green-200 px-3 py-1">
                                              <Clock className="w-3.5 h-3.5 mr-1.5" />
                                              {session.duration_minutes} min
                                            </Badge>
                                          )}
                                        </div>

                                        {/* Session Summary */}
                                        <div className="mb-4">
                                          <div className="flex items-center gap-2 mb-3">
                                            <Sparkles className="w-4 h-4 text-purple-500" />
                                            <h4 className="font-semibold text-slate-800">FaheemGPT Summary</h4>
                                          </div>
                                          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-5 border border-purple-100">
                                            {session.summary ? (
                                              formatSummaryText(session.summary)
                                            ) : (
                                              <p className="text-sm text-slate-500 italic">No summary available for this session.</p>
                                            )}
                                          </div>
                                        </div>

                                        {/* Topics Covered */}
                                        {session.topics && session.topics.length > 0 && (
                                          <div className="mb-4">
                                            <div className="flex items-center gap-2 mb-2">
                                              <BookMarked className="w-4 h-4 text-blue-500" />
                                              <h4 className="font-semibold text-slate-800">Topics Covered</h4>
                                              <Badge variant="outline" className="ml-auto text-xs text-slate-500">
                                                {session.topics.length} topics
                                              </Badge>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                              {session.topics.map((topic, topicIdx) => (
                                                <Badge 
                                                  key={topicIdx} 
                                                  className="bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1 text-sm"
                                                >
                                                  <CheckCircle2 className="w-3 h-3 mr-1.5" />
                                                  {topic}
                                                </Badge>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {/* Key Learnings - if available */}
                                        {session.key_points && session.key_points.length > 0 && (
                                          <div className="mb-4">
                                            <div className="flex items-center gap-2 mb-2">
                                              <Lightbulb className="w-4 h-4 text-amber-500" />
                                              <h4 className="font-semibold text-slate-800">Key Learnings</h4>
                                            </div>
                                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                                              <ul className="space-y-2">
                                                {session.key_points.map((point, pointIdx) => (
                                                  <li key={pointIdx} className="flex items-start gap-2 text-sm text-slate-700">
                                                    <ChevronRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                                    {point}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          </div>
                                        )}

                                        {/* Transcript Preview */}
                                        {session.transcript && (
                                          <Accordion type="single" collapsible className="w-full">
                                            <AccordionItem value="transcript" className="border border-slate-200 rounded-xl overflow-hidden">
                                              <AccordionTrigger className="px-4 py-3 text-sm text-slate-600 hover:no-underline hover:bg-slate-50">
                                                <span className="flex items-center gap-2">
                                                  <FileTextIcon className="w-4 h-4 text-slate-500" />
                                                  <span className="font-medium">View Full Conversation Transcript</span>
                                                </span>
                                              </AccordionTrigger>
                                              <AccordionContent className="px-4 pb-4">
                                                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 max-h-64 overflow-y-auto whitespace-pre-wrap font-mono border border-slate-200">
                                                  {session.transcript}
                                                </div>
                                              </AccordionContent>
                                            </AccordionItem>
                                          </Accordion>
                                        )}

                                        {/* Session Stats Footer */}
                                        {(session.questions_asked || session.concepts_explained) && (
                                          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
                                            {session.questions_asked && (
                                              <span className="flex items-center gap-1">
                                                <HelpCircle className="w-3.5 h-3.5" />
                                                {session.questions_asked} questions asked
                                              </span>
                                            )}
                                            {session.concepts_explained && (
                                              <span className="flex items-center gap-1">
                                                <Brain className="w-3.5 h-3.5" />
                                                {session.concepts_explained} concepts explained
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-12 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-xl border-2 border-dashed border-slate-200">
                                    <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                      <FileTextIcon className="w-8 h-8 text-indigo-400" />
                                    </div>
                                    <p className="text-slate-700 font-medium">No session summaries yet</p>
                                    <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                                      Start a learning session with FaheemGPT to see your progress and summaries here
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 pt-2">
                            <Link to={course.link || `/course/${course.id}`} className="flex-1">
                              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                                Continue Learning
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                            <Button 
                              variant="outline" 
                              size="icon"
                              onClick={() => handleUnenroll(course.id, course.title)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-slate-50 border-dashed border-2">
                <CardContent className="py-16 text-center">
                  <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <BookOpen className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No Courses Yet</h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Start your learning journey by enrolling in a course that interests you.
                  </p>
                  <Link to="/courses">
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                      Explore Courses
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-6">
            <h2 className="text-xl font-bold text-slate-900">Recent Activity</h2>
            
            {recentActivities.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {recentActivities.map((activity, index) => {
                      const ActivityIcon = activityTypeIcons[activity.type] || Activity;
                      return (
                        <div 
                          key={activity.id || index}
                          className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="bg-purple-100 p-3 rounded-xl">
                            <ActivityIcon className="w-5 h-5 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">
                              {activityTypeLabels[activity.type] || activity.type}
                            </p>
                            {activity.details && (
                              <p className="text-sm text-slate-500">{activity.details}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">
                              {formatActivityDate(activity.date)}
                            </p>
                            {activity.duration > 0 && (
                              <Badge variant="outline" className="mt-1">
                                {activity.duration}m
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-50 border-dashed border-2">
                <CardContent className="py-12 text-center">
                  <Activity className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-700">No Activity Yet</h3>
                  <p className="text-sm text-slate-500 mt-1">Start learning to see your activity here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Explore Tab */}
          <TabsContent value="explore" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Discover Courses</h2>
              <Link to="/courses">
                <Button variant="outline">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allCourses.filter(c => !profile?.enrolled_courses?.includes(c.id)).slice(0, 6).map((course) => {
                const isProcessing = enrolling[course.id];
                const offeredBy = course.offered_by;
                const institutionIds = Array.isArray(offeredBy) ? offeredBy : [offeredBy];
                const courseInstitutions = institutionIds
                  .map(id => institutions[id])
                  .filter(inst => inst);

                return (
                  <Card key={course.id} className="hover:shadow-lg transition-all hover:-translate-y-1">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <Badge variant="outline" className="text-xs">
                          {course.level || 'Beginner'}
                        </Badge>
                        {course.status === 'coming_soon' && (
                          <Badge className="bg-slate-200 text-slate-600">Coming Soon</Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{course.title}</h3>
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{course.description}</p>
                      
                      {courseInstitutions.length > 0 && (
                        <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                          {courseInstitutions.slice(0, 2).map((inst, idx) => (
                            <img 
                              key={idx}
                              src={inst.logo_url} 
                              alt={inst.name}
                              className="w-5 h-5 object-contain rounded"
                              title={inst.name}
                            />
                          ))}
                          {courseInstitutions.length > 0 && (
                            <span className="text-xs text-slate-500">{courseInstitutions[0].name}</span>
                          )}
                        </div>
                      )}

                      <Button 
                        className="w-full"
                        onClick={() => handleEnroll(course.id, course.title)}
                        disabled={isProcessing || course.status === 'coming_soon'}
                      >
                        {isProcessing ? 'Enrolling...' : 'Enroll Now'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {allCourses.filter(c => !profile?.enrolled_courses?.includes(c.id)).length === 0 && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="py-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-green-900">You&apos;re enrolled in all courses!</h3>
                  <p className="text-sm text-green-700 mt-1">Great job staying on top of your learning.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Grades Tab */}
          <TabsContent value="grades" className="space-y-6">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-600" />
                      My Grades
                    </CardTitle>
                    <CardDescription>
                      View your assignment grades and feedback from teachers
                    </CardDescription>
                  </div>
                  {myGrades.length > 0 && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">
                        {Math.round(myGrades.reduce((acc, g) => acc + (g.grade || 0), 0) / myGrades.length)}
                      </p>
                      <p className="text-xs text-slate-500">Average Grade</p>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {myGrades.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Grades Yet</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                      Submit assignments in your courses to receive grades and feedback from your teachers.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myGrades.map((grade, index) => (
                      <div 
                        key={grade.id || index}
                        className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">{grade.assignment_title}</h4>
                            <p className="text-sm text-slate-600">{grade.course_title}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Graded: {new Date(grade.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className={`text-3xl font-bold ${
                              grade.grade >= 90 ? 'text-green-600' :
                              grade.grade >= 80 ? 'text-blue-600' :
                              grade.grade >= 70 ? 'text-amber-600' :
                              grade.grade >= 60 ? 'text-orange-600' :
                              'text-red-600'
                            }`}>
                              {grade.grade}
                            </div>
                            <p className="text-xs text-slate-500">/ {grade.max_points || 100}</p>
                          </div>
                        </div>
                        {grade.feedback && (
                          <div className="mt-3 p-3 bg-white rounded border border-slate-200">
                            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" /> Teacher Feedback
                            </p>
                            <p className="text-sm text-slate-700">{grade.feedback}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Formative Feedback Assessment */}
            <FormativeFeedback />

            {/* Cognitive Growth Graph */}
            <CognitiveGrowthGraph />

            {/* Quiz History Section */}
            <QuizHistory />

            {/* Teacher Dashboard Link */}
            {isTeacher && (
              <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Teacher Dashboard</h3>
                        <p className="text-sm text-slate-600">You have teacher access. Manage students and grade assignments.</p>
                      </div>
                    </div>
                    <Button onClick={() => navigate('/teacher-profile')} className="bg-blue-600 hover:bg-blue-700">
                      Open Dashboard
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Learning Insights Tab */}
          <TabsContent value="insights" className="space-y-6">
            <LearningInsights />
          </TabsContent>

          {/* Skills Dashboard Tab */}
          <TabsContent value="skills" className="space-y-6">
            <SkillsDashboard />
          </TabsContent>

          {/* Peer Insights Tab */}
          <TabsContent value="peers" className="space-y-6">
            <PeerInsights />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <MessagingPanel userType="student" students={myTeachers} />
          </TabsContent>

          {/* Resources Hub Tab */}
          <TabsContent value="resources" className="space-y-6">
            <ResourcesHub 
              enrolledCourses={courses} 
              token={token}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white border shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Edit3 className="w-5 h-5 text-blue-600" />
              Edit Profile
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Update your profile information. Click save when you&apos;re done.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700 font-medium">Display Name</Label>
              <Input
                id="username"
                value={editForm.username}
                onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Your display name"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="avatar_url" className="text-slate-700 font-medium">Profile Image URL</Label>
              <Input
                id="avatar_url"
                value={editForm.avatar_url}
                onChange={(e) => setEditForm(prev => ({ ...prev, avatar_url: e.target.value }))}
                placeholder="https://example.com/your-image.jpg"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
              />
              {editForm.avatar_url && (
                <div className="mt-2 flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  <img 
                    src={editForm.avatar_url} 
                    alt="Preview" 
                    className="w-12 h-12 rounded-lg object-cover border border-slate-200"
                    onError={(e) => { e.target.src = ''; e.target.alt = 'Invalid URL'; }}
                  />
                  <span className="text-xs text-slate-600">Image preview</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio" className="text-slate-700 font-medium">Bio</Label>
              <Textarea
                id="bio"
                value={editForm.bio}
                onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about yourself..."
                rows={3}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location" className="text-slate-700 font-medium">Location</Label>
                <Input
                  id="location"
                  value={editForm.location}
                  onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="City, Country"
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="occupation" className="text-slate-700 font-medium">Occupation</Label>
                <Input
                  id="occupation"
                  value={editForm.occupation}
                  onChange={(e) => setEditForm(prev => ({ ...prev, occupation: e.target.value }))}
                  placeholder="Student, Developer, etc."
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="border-slate-300 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button onClick={handleProfileUpdate} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
            <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors">Terms</Link>
            <Link to="/support" className="hover:text-slate-900 transition-colors">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
